import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';

import { type ClassSearchDetails } from '../modal/class-search';

export type Days = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';

export interface GetAllClassParams {
  page?: number; // Optional pagination parameter
  limit?: number; // Optional limit for number of results
  session?: string; // Optional parameter for filtering by session
  sessionId?: string; // Optional parameter for filtering by session ID
  days?: Days; // Optional parameter for filtering by days
  instructor?: string; // Optional parameter for filtering by instructor
  name?: string; // Optional parameter for filtering by class name
  room?: string; // Optional parameter for filtering by room
  tags?: string; // Optional parameter for filtering by tags
  /** Single age (Age dropdown). Mapped to minAge=maxAge for the API. */
  age?: string;
  minAge?: number;
  maxAge?: number;
  location?: string; // Optional parameter for filtering by location
  studio?: boolean; // Optional parameter for filtering by studio
  showPastClasses?: boolean; // Optional parameter for showing past/expired classes
  isArchive?: boolean; // Optional parameter for filtering archived classes
  /** Restrict results to the page's past/archived toggles (All Classes list) */
  enforceStatusFilters?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  sortByUpcoming?: boolean;
  trialClassesOnly?: boolean;
  privateLessonsOnly?: boolean;
}

export interface GetAllClassResponse {
  data: {
    classes: Array<ClassSearchDetails>;
    rooms: Array<{
      _id: string;
      name: string;
      fieldType: 'room';
    }>;
    ages?: {
      minAge: number;
      maxAge: number;
    };
    instructors: Array<{
      _id: string;
      name: string;
      fieldType: 'instructor';
    }>;
    tags: Array<{
      _id: string;
      name: string;
      fieldType: 'tags';
    }>;
    sessions: Array<{
      _id: string;
      name: string;
      fieldType: 'session';
    }>;
    locations: Array<{
      _id: string;
      name: string;
      fieldType: 'location';
    }>;
  }; // Array of class details
  pagination: Pagination; // Pagination information
}

export interface Pagination {
  page: number; // Current page number
  limit: number; // Limit of items per page
  totalCount: number; // Total number of items
  totalPages: number; // Total number of pages
}

/**
 * Map Age dropdown `age` → minAge/maxAge so GET enrollments/search/class
 * receives params the backend recognizes (ENR-2169).
 */
export function toClassSearchRequestParams(params: GetAllClassParams) {
  const { age, minAge, maxAge, ...rest } = params;
  const parsedAge =
    age !== undefined && age !== '' && age !== 'All' ? Number(age) : undefined;
  const resolvedMinAge =
    minAge ??
    (parsedAge !== undefined && !Number.isNaN(parsedAge)
      ? parsedAge
      : undefined);
  const resolvedMaxAge =
    maxAge ??
    (parsedAge !== undefined && !Number.isNaN(parsedAge)
      ? parsedAge
      : undefined);

  return {
    ...rest,
    ...(resolvedMinAge !== undefined ? { minAge: resolvedMinAge } : {}),
    ...(resolvedMaxAge !== undefined ? { maxAge: resolvedMaxAge } : {}),
  };
}

// API call to fetch all classes based on provided parameters
export const getAllClassesWithSearchAPI = async (params: GetAllClassParams) => {
  const response = await baseAPI.get<GetAllClassResponse>(
    `enrollments/search/class`,
    {
      params: toClassSearchRequestParams(params),
    },
  );
  return response.data; // Return the data from the response
};

// Options for the query to fetch all classes
export const getAllClassesWithSearchOptions = (params: GetAllClassParams) => {
  return queryOptions({
    queryKey: QUERY_KEYS.CLASSES(params), // Unique key for the query
    queryFn: () => getAllClassesWithSearchAPI(params), // Function to fetch data

    staleTime: 1000 * 60 * 5,
  });
};

// Custom hook to use the query for fetching all classes
export const useGetAllClassesWithSearch = (params: GetAllClassParams) => {
  return useQuery(getAllClassesWithSearchOptions(params)); // Use the query with options
};

const DEFAULT_INFINITE_PAGE_SIZE = 30;

type GetAllClassesInfiniteParams = Omit<GetAllClassParams, 'page'>;

export const useGetAllClassesWithSearchInfinite = (
  params: GetAllClassesInfiniteParams,
  options?: { enabled?: boolean },
) => {
  const limit = params.limit ?? DEFAULT_INFINITE_PAGE_SIZE;
  const paramsForKey: GetAllClassParams = { ...params, limit, page: undefined };

  return useInfiniteQuery({
    queryKey: [...QUERY_KEYS.CLASSES(paramsForKey), 'infinite'] as const,
    queryFn: ({ pageParam }) =>
      getAllClassesWithSearchAPI({
        ...params,
        limit,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      if (totalPages === 0 || page >= totalPages) return undefined;
      return page + 1;
    },
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled,
  });
};
